import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import Navbar from "../../components/common/Navbar";
import Footer from "../../components/common/Footer";
import { OrderStatusBadge, Spinner, EmptyState } from "../../components/common/UI";
import toast from "react-hot-toast";

// ── UserOrdersPage ────────────────────────────────────────────────────────────
export function UserOrdersPage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [orderItems, setOrderItems] = useState({});
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from("orders")
      .select("*, restaurants(name, slug, logo_url)")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .then(({ data }) => {
        setOrders(data || []);
        setLoading(false);
      });
  }, [user]);

  const fetchItems = async (orderId) => {
    if (orderItems[orderId]) {
      setExpandedOrder(expandedOrder === orderId ? null : orderId);
      return;
    }
    const { data } = await supabase
      .from("order_items")
      .select("*")
      .eq("order_id", orderId);
    setOrderItems((p) => ({ ...p, [orderId]: data || [] }));
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  return (
    <>
      <Helmet><title>My Orders — Khana Spot</title></Helmet>
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 page-enter">
        <h1 className="font-display text-2xl font-bold text-gray-900 mb-6">My Orders</h1>
        {loading ? (
          <div className="flex justify-center py-16"><Spinner size="lg" /></div>
        ) : orders.length === 0 ? (
          <EmptyState
            icon="📦"
            title="No orders yet"
            description="Start exploring restaurants and place your first order!"
            action={<Link to="/restaurants" className="btn-primary">Browse Restaurants</Link>}
          />
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div key={order.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    {order.restaurants?.logo_url ? (
                      <img src={order.restaurants.logo_url} alt="" className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0 text-xl">🍽️</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div>
                          <Link
                            to={`/restaurant/${order.restaurants?.slug}`}
                            className="font-semibold text-gray-900 hover:text-orange-600 transition-colors"
                          >
                            {order.restaurants?.name}
                          </Link>
                          <p className="text-xs text-gray-400 mt-0.5 font-mono">
                            #{order.id.slice(0, 8).toUpperCase()}
                          </p>
                        </div>
                        <OrderStatusBadge status={order.status} />
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <span className="font-bold text-orange-600">
                          Rs {order.total?.toLocaleString()}
                        </span>
                        <span className="text-gray-400 text-xs">
                          {new Date(order.created_at).toLocaleDateString("en-PK", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100">
                    <button
                      onClick={() => fetchItems(order.id)}
                      className="flex items-center gap-1.5 text-xs border border-gray-200 px-3 py-2 rounded-xl font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                    >
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expandedOrder === order.id ? "rotate-180" : ""}`} />
                      View Items
                    </button>
                    {order.status === "delivered" && (
                      <Link
                        to={`/restaurant/${order.restaurants?.slug}#reviews`}
                        className="text-xs text-orange-500 font-medium border border-orange-200 px-3 py-2 rounded-xl hover:bg-orange-50 transition-colors"
                      >
                        ⭐ Leave Review
                      </Link>
                    )}
                    <Link
                      to={`/restaurant/${order.restaurants?.slug}`}
                      className="text-xs text-gray-500 font-medium border border-gray-200 px-3 py-2 rounded-xl hover:bg-gray-50 transition-colors ml-auto"
                    >
                      Reorder →
                    </Link>
                  </div>
                </div>

                {expandedOrder === order.id && orderItems[order.id] && (
                  <div className="border-t border-gray-100 bg-gray-50 px-5 py-4">
                    <div className="space-y-2">
                      {orderItems[order.id].map((item) => (
                        <div key={item.id} className="flex justify-between text-sm">
                          <span className="text-gray-700">
                            {item.menu_item_name}{" "}
                            <span className="text-gray-400">× {item.quantity}</span>
                          </span>
                          <span className="font-semibold text-gray-900">Rs {item.subtotal}</span>
                        </div>
                      ))}
                    </div>
                    <div className="pt-2 border-t border-gray-200 mt-2 flex justify-between font-bold text-sm">
                      <span>Total</span>
                      <span className="text-orange-600">Rs {order.total?.toLocaleString()}</span>
                    </div>
                    {order.customer_address && (
                      <p className="text-xs text-gray-400 mt-2">📍 {order.customer_address}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}

// ── UserProfilePage ───────────────────────────────────────────────────────────
export function UserProfilePage() {
  const { user, profile, fetchProfile } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (profile) setForm({ name: profile.name || "", phone: profile.phone || "" });
  }, [profile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const { error } = await supabase.from("users").update(form).eq("id", user.id);
    if (error) {
      toast.error("Failed to save");
    } else {
      await fetchProfile(user.id);
      toast.success("Profile updated!");
    }
    setSaving(false);
  };

  return (
    <>
      <Helmet><title>My Profile — Khana Spot</title></Helmet>
      <Navbar />
      <div className="max-w-lg mx-auto px-4 py-12 page-enter">
        <h1 className="font-display text-2xl font-bold text-gray-900 mb-6">My Profile</h1>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center text-2xl font-bold text-orange-600">
              {(profile?.name || user?.email || "U")[0].toUpperCase()}
            </div>
            <div>
              <p className="font-display font-bold text-xl text-gray-900">{profile?.name}</p>
              <p className="text-gray-500 text-sm">{user?.email}</p>
              <span className="badge bg-orange-100 text-orange-700 mt-1 capitalize">{profile?.role}</span>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                Full Name
              </label>
              <input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="input-field"
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                Phone Number
              </label>
              <input
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                className="input-field"
                placeholder="03XX-XXXXXXX"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                Email
              </label>
              <input
                value={user?.email}
                disabled
                className="input-field bg-gray-50 text-gray-400 cursor-not-allowed"
              />
            </div>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {saving && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default UserOrdersPage;
